import { useDispatch, useSelector } from 'react-redux';
import PitchSlider from './PitchSlider';
import { DivPanelCount, HeadingAzimuth, DivPitchWrap,
	DivFacetDetail, SystemDivWrap, ArrayHeading, FlexTwoCol, 
	HeightSpacingWrap} from './style';
import { AppDispatch } from 'store';
import { toggleManualToolDoneButtonState, toggleManualToolResetButtonState } from 'store/slices/ManualToolModal';
import { GROUND_MOUNT_DEFAULT_PITCH, KONVA_FACET_GROUP } from '../../constants';
import { FacetPropsKeyValue } from 'components/Slider/styles';
import { selectIsRecomputing } from 'store/slices/DsmShadingSlice/selectors';
import ShadingField from '../FacetInputs/ShadingField';
import RowSpacing from '../FacetInputs/RowSpacing';
import StartingHeight from '../FacetInputs/StartingHeight';

export function AzimuthDiv(props: { azimuthDegrees: number }) {
	const roundedValue = !(props.azimuthDegrees == null) ? Math.round(props.azimuthDegrees) : 'N/A';
	return (
		<HeadingAzimuth className='default-azimuth'>
			<label>Azimuth:</label>
			<span>{roundedValue}</span>
		</HeadingAzimuth>
	);
}

export function PitchDiv(props: { pitchDegrees: number }) {
	const roundedValue = Math.round(props.pitchDegrees);
	return (
		<DivPitchWrap>
			<label>Pitch:</label>
			<span>{roundedValue}</span>
		</DivPitchWrap>
	);
}
export function PanelsCountDiv(props: { panelsCount: number; className?: string; }) {
	return (

		<DivPanelCount className={props.className}>
			<label>Panels Count:</label>
			<span>{props.panelsCount} </span>
		</DivPanelCount >
	);
}

export function SystemProductionDiv(props: { systemProduction: number; className?: string; }) {
	const roundedValue = (+props.systemProduction.toFixed(2));
	return (
		<DivPanelCount className={props.className}>
			<label>System Production:</label>
			<span>{roundedValue} kWh</span>
		</DivPanelCount>
	);
}

export function SystemSize(props: { systemSize: number; className?: string; }) {
	const roundedValue = +props.systemSize.toFixed(2);
	return (
		<DivPanelCount className={props.className}>
			<label>System Size:</label>
			<span>{roundedValue} kW</span>
		</DivPanelCount>
	);
}

type Props = {
	groupSuffix: string;
	konvaPolygonId: string;
	active?: boolean;
	onChange?: (id: string, groupSuffix: string) => void;
	azimuthDegrees?: number;
	pitchDegrees?: number | null;
	systemSize?: number;
	systemProduction?: number;
	panelsCount?: number;
	previousPitch?: number;
	previousShading?: number;
	isGroundMount? : boolean;
	noGapGroundMount?: boolean;
	enableStartingHeightEdit?: boolean;
	enableRowSpacingEdit?: boolean;
	enableShadingEdit : boolean;
	shading? : number;
	rowSpacing?: number;
	startingHeight?: number;
	onPitchValChange?: (id: string, groupSuffix: string, value: any) => void;
	onShadingValChange?: (id: string, groupSuffix: string, value: any)=> void;
	onRowSpacingValChange?: (id: string, groupSuffix: string, value: any) => void;
	onStartingHeightValChange?: (id: string, groupSuffix: string, value: any) => void;
	area?: number;
	isEdited?: boolean;
	canEditFacetProps: boolean;
	totalPanelsFromFacet?: number; 
	totalPanelsProduction?: number;
	totalSystemSize?: number;
	showCompleteArrayDetails?: boolean;
};
export default function DefaultModeValues(props: Props) {
	const { azimuthDegrees, pitchDegrees, shading, rowSpacing, startingHeight, isGroundMount, noGapGroundMount,
		enableStartingHeightEdit, enableRowSpacingEdit, enableShadingEdit, groupSuffix, systemProduction, konvaPolygonId, systemSize, panelsCount,
		canEditFacetProps, totalPanelsFromFacet, totalPanelsProduction, totalSystemSize, showCompleteArrayDetails } = props;
	const dispatch = useDispatch<AppDispatch>();
	const isRecomputing = useSelector(selectIsRecomputing);

	function handleClick() {
		props.onChange && props.onChange(konvaPolygonId, groupSuffix);
	}

	// Flag the facet as edited so the Done/Reset buttons enable (skipped while still drawing).
	function markEdited() {
		props.onChange && props.onChange(konvaPolygonId, groupSuffix);
		if (groupSuffix.slice(1) != KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME) {
			dispatch(toggleManualToolDoneButtonState({ inputFieldsEdited: true }));
			dispatch(toggleManualToolResetButtonState({ resetEnabled: true }));
		}
	}

	function handlePitchChange(v: string | number) {
		props.onPitchValChange && props.onPitchValChange(konvaPolygonId, groupSuffix, +v);
		markEdited();
	}

	function handleShadingChange(value: number) {
		props.onShadingValChange && props.onShadingValChange(konvaPolygonId, groupSuffix, value);
		markEdited();
	}

	function handleRowSpacingChange(value: number) {
		props.onRowSpacingValChange && props.onRowSpacingValChange(konvaPolygonId, groupSuffix, value);
		// value is NaN while the field is cleared mid-edit (see RowSpacing.tsx) - forward it so the
		// pending debounced write resets, but don't mark the facet edited for a non-final value
		if (!Number.isNaN(value)) markEdited();
	}

	function handleStartingHeightChange(value: number) {
		props.onStartingHeightValChange && props.onStartingHeightValChange(konvaPolygonId, groupSuffix, value);
		if (!Number.isNaN(value)) markEdited();
	}

	return (
		<>
			<DivFacetDetail className={`${props.active ? 'active' : ''} ${props.isEdited ? 'edited' : ''}`} onClick={handleClick}>
				<AzimuthDiv azimuthDegrees={azimuthDegrees!} />
				<SystemProductionDiv systemProduction={systemProduction ?? 0} />
				<FlexTwoCol>
					<SystemSize systemSize={systemSize ?? 0} />
					<PanelsCountDiv panelsCount={panelsCount ?? 0} />
				</FlexTwoCol>
				{<div className='facet-slider-wrap facet-slider-slope-info'>
					{
						canEditFacetProps ? 
							(<PitchSlider
								pitch={pitchDegrees ?? GROUND_MOUNT_DEFAULT_PITCH}
								active= {!!props.active }
								onChange={handlePitchChange}
								disabled={isRecomputing}
							/>) :
							<FacetPropsKeyValue>
										Pitch:
								<span className="value">{Math.round(pitchDegrees ?? GROUND_MOUNT_DEFAULT_PITCH )* 100 / 100}</span>
							</FacetPropsKeyValue>
					}
				</div>}
				<ShadingField
					enableShadingEdit={enableShadingEdit}
					canEditFacetProps={canEditFacetProps}
					isGroundMount={isGroundMount}
					shading={shading}
					onChange={handleShadingChange}
				/>

				{
					(isGroundMount && canEditFacetProps) && (() => {
						const showRowSpacing = !noGapGroundMount && enableRowSpacingEdit;
						const showStartingHeight = enableStartingHeightEdit;
						return (showRowSpacing || showStartingHeight) && (
							<HeightSpacingWrap>
								{showRowSpacing && (
									<RowSpacing
										value={rowSpacing}
										onChange={handleRowSpacingChange}
										disabled={isRecomputing}
										singleField={!showStartingHeight}
									/>
								)}

								{showStartingHeight && (
									<StartingHeight
										value={startingHeight}
										onChange={handleStartingHeightChange}
										disabled={isRecomputing}
										singleField={!showRowSpacing}
									/>
								)}
							</HeightSpacingWrap>
						);
					})()
				}

				{(showCompleteArrayDetails && (panelsCount != totalPanelsFromFacet)) &&
					<SystemDivWrap className={`${props.active ? 'active' : ''}`}>
						<ArrayHeading>Complete array details</ArrayHeading> 
						<SystemProductionDiv className='array-detail-value array-production' systemProduction={totalPanelsProduction!} />
						<FlexTwoCol>
							<SystemSize className='array-detail-value' systemSize={totalSystemSize!} />
							<PanelsCountDiv className='array-detail-value' panelsCount={totalPanelsFromFacet!} />
						</FlexTwoCol>
					</SystemDivWrap>
				} 
			</DivFacetDetail>
			
		</>
	);

}